import { useQuery, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { GET_PLOTS_PATH, GetPlotsErrorDto, GetPlotsSuccessDto } from '@/api/rest/resources/types/plot';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';

import { useNCAParentOrganizationId } from './useNCAParentOrganizationId';
import { useOrganizationId } from './useOrganizationId';

type SuccessDto = GetPlotsSuccessDto;
type ErrorDto = GetPlotsErrorDto;

export const SKIP_PLOT_GROUP_ID = 'skip_plot_group_id';
export const PLOTS_FETCH_LIMIT = 1000;

const useGetPlotsForOrganizationQueryParams = (
  organizationIdArg?: string,
  filters?: Record<string, unknown> | null,
  offset?: number,
  plotGroupIdArg?: string,
  parentPlotGroupIdArg?: string,
  limit: number = PLOTS_FETCH_LIMIT,
) => {
  if (limit > PLOTS_FETCH_LIMIT) {
    throw new Error(
      `The Frontend limit to fetch plots cannot be greater than the Backend limit of ${PLOTS_FETCH_LIMIT}.`,
    );
  }

  const orgId = useOrganizationId();
  const plotGroupId = usePlotGroupId();
  const ncaParentId = useNCAParentOrganizationId();
  const workstreamId = useWorkstreamId();

  return {
    organization: organizationIdArg ?? orgId,
    ...filters,
    limit,
    offset,
    plot_group_id: plotGroupIdArg === SKIP_PLOT_GROUP_ID ? undefined : (plotGroupIdArg ?? plotGroupId),
    parent_plot_group_id: parentPlotGroupIdArg,
    ...(ncaParentId ? { nca_parent_id: ncaParentId } : {}),
    ...(workstreamId ? { workstream_id: workstreamId } : {}),
  };
};

const queryFn = async (queryParams: ReturnType<typeof useGetPlotsForOrganizationQueryParams>) => {
  const response = await getRequest<SuccessDto>(GET_PLOTS_PATH, undefined, queryParams);
  if (response.results.length > 0) {
    return response;
  }
  /**
   * If there are no results, we default to the first page. This handles the
   * edge case where page no. 5 has only one plot, and if we delete that plot,
   * we end up with a table with no items in it.
   */
  return getRequest<SuccessDto>(GET_PLOTS_PATH, undefined, { ...queryParams, offset: 0 });
};

export const usePlotsForOrganization = (
  organizationIdArg?: string,
  filters?: Record<string, unknown> | null,
  limit?: number,
  offset?: number,
  plotGroupIdArg?: string,
  parentPlotGroupIdArg?: string,
) => {
  const queryParams = useGetPlotsForOrganizationQueryParams(
    organizationIdArg,
    filters,
    offset,
    plotGroupIdArg,
    parentPlotGroupIdArg,
    limit,
  );

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['plots', 'organization', queryParams],
    queryFn: () => queryFn(queryParams),
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * This hook fetches the plot table, which can be a heavy query. So by passing
 * "enabled" as false, we can control when to fetch the data.
 */
export const useEnabledPlotsForOrganization = (
  organizationIdArg?: string,
  filters?: Record<string, unknown> | null,
  limit?: number,
  offset?: number,
  plotGroupIdArg?: string,
  parentPlotGroupIdArg?: string,
  enabled = false,
) => {
  const queryParams = useGetPlotsForOrganizationQueryParams(
    organizationIdArg,
    filters,
    offset,
    plotGroupIdArg,
    parentPlotGroupIdArg,
    limit,
  );

  return useQuery<SuccessDto, ErrorDto>({
    queryKey: ['plots', 'organization', queryParams, 'enabled', enabled],
    queryFn: () => queryFn(queryParams),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

export const useAllPlotsForOrganization = (
  organizationIdArg?: string,
  filtersArg?: Record<string, unknown> | null,
  plotGroupIdArg?: string,
  parentPlotGroupIdArg?: string,
) => {
  const filters = filtersArg ?? { fact_names: 'None' };
  const firstPage = usePlotsForOrganization(
    organizationIdArg,
    filters,
    PLOTS_FETCH_LIMIT,
    0,
    plotGroupIdArg,
    parentPlotGroupIdArg,
  );

  const totalCount = firstPage.data.count;
  const remainingPagesCount = Math.max(0, Math.ceil((totalCount - PLOTS_FETCH_LIMIT) / PLOTS_FETCH_LIMIT));

  const queryParams = useGetPlotsForOrganizationQueryParams(
    organizationIdArg,
    filters,
    undefined,
    plotGroupIdArg,
    parentPlotGroupIdArg,
    PLOTS_FETCH_LIMIT,
  );

  const remainingPages = useSuspenseQueries({
    queries: Array.from({ length: remainingPagesCount }, (_, i) => {
      const newQueryParams = {
        ...queryParams,
        offset: (i + 1) * PLOTS_FETCH_LIMIT,
      };
      return {
        queryKey: ['plots', 'organization', newQueryParams],
        queryFn: () => getRequest<SuccessDto>(GET_PLOTS_PATH, undefined, newQueryParams),
        staleTime: 1000 * 60 * 5,
      };
    }),
  });

  const allResults = [...firstPage.data.results, ...remainingPages.map((page) => page.data.results).flat()];

  return {
    ...firstPage,
    data: {
      ...firstPage.data,
      results: allResults,
      count: totalCount,
    },
  };
};
